import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { DiscountOfferModal } from './DiscountOfferModal';
import { PricingStep } from './PricingStep';
import { fireSideCannons } from './fireSideCannons';
import {
  ONBOARDING_DISCOUNT_PERCENT_OFF,
  useIssueOnboardingDiscount,
  useOnboardingDiscount,
} from './onboardingDiscount';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { SCENARIO_ID } from '@/context';

export const SKIP_CONFETTI_KEY_PREFIX = '__qc_onboarding_skip_confetti:';

interface PricingStepContainerProps {
  onBack: () => void;
  onSkipForNow: () => void | Promise<void>;
  isSkipPending?: boolean;
}

export const PricingStepContainer = ({
  onBack,
  onSkipForNow,
  isSkipPending = false,
}: PricingStepContainerProps) => {
  const { t } = useTranslation('getting_started');

  const { state: discount, isClaimable } = useOnboardingDiscount();
  const issueDiscount = useIssueOnboardingDiscount();
  const { capture } = usePostHogAnalytics();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSkipClick = useCallback(() => {
    if (discount && isClaimable) {
      sessionStorage.setItem(`${SKIP_CONFETTI_KEY_PREFIX}${SCENARIO_ID}`, '1');
      void onSkipForNow();
      return;
    }
    issueDiscount.mutate(undefined, {
      onSuccess: (data) => {
        // 204 (e.g. ineligible after upgrading in another tab) returns no code —
        // skip silently instead of opening an empty modal + firing confetti for
        // nothing.
        if (!data?.code) {
          void onSkipForNow();
          return;
        }
        setIsModalOpen(true);
        fireSideCannons();
        // Funnel denominator: the offer was actually shown to the user.
        capture(PostHogEventEnum.GettingStartedDiscountOffered);
      },
      onError: () => {
        // Issuance failed (Stripe outage, etc). Don't trap the user on Pricing
        // — surface the failure and let Skip advance them through.
        toast.error(t('discount_modal.skip_fallback_toast'));
        void onSkipForNow();
      },
    });
  }, [discount, isClaimable, issueDiscount, onSkipForNow, capture, t]);

  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const handleClaimDiscount = useCallback(() => {
    capture(PostHogEventEnum.GettingStartedDiscountClaimed);
    closeModal();
  }, [capture, closeModal]);

  const handleUseLater = useCallback(() => {
    capture(PostHogEventEnum.GettingStartedDiscountUseLater);
    closeModal();
  }, [capture, closeModal]);

  const isDiscountActive = isClaimable;

  return (
    <div
      className="flex flex-col gap-6 bg-card border border-border rounded-xl p-6 min-h-[500px]"
      data-testid="gs-pricing-container"
    >
      <div>
        <p
          className="text-xl font-semibold text-foreground"
          data-testid="gs-pricing-container-title"
        >
          {t('steps.gs_step_5.title')}
        </p>
        <p
          className="text-sm font-medium text-muted-foreground mt-1"
          data-testid="gs-pricing-container-subtitle"
        >
          <Trans
            i18nKey="steps.gs_step_5.description"
            t={t}
            components={{ bold: <strong className="font-semibold text-foreground" /> }}
          />
        </p>
      </div>

      <div className="flex flex-1 flex-col">
        <PricingStep
          discountApplied={isDiscountActive}
          discountPercentOff={ONBOARDING_DISCOUNT_PERCENT_OFF}
          promoCode={isDiscountActive ? discount?.code : undefined}
        />
      </div>

      <div className="flex items-center justify-end gap-3">
        <Button
          size="sm"
          variant="link"
          onClick={onBack}
          className="!text-foreground/60 hover:!opacity-100 hover:!text-foreground"
          data-testid="gs-pricing-container-back-button"
        >
          <ChevronLeft />
          {t('back')}
        </Button>
        <Button
          size="sm"
          variant="link"
          onClick={handleSkipClick}
          disabled={isSkipPending || issueDiscount.isPending}
          className="!text-foreground/60 hover:!opacity-100 hover:!text-foreground"
          data-testid="gs-pricing-container-skip-button"
        >
          {isSkipPending || issueDiscount.isPending ? (
            <Spinner className="size-4" />
          ) : (
            <>
              {t('pricing_step.skip_for_now')}
              <ChevronRight />
            </>
          )}
        </Button>
      </div>

      {discount && (
        <DiscountOfferModal
          open={isModalOpen}
          code={discount.code}
          expiresAt={new Date(discount.expires_at).getTime()}
          onClose={closeModal}
          onRedeem={handleClaimDiscount}
          onUseLater={handleUseLater}
        />
      )}
    </div>
  );
};
